import React, { useCallback, useRef, useState, useEffect } from "react";
import { useLoadMore } from "../Council/News/useLoadMore";
import { useDispatch, useSelector } from "react-redux";
import {
  viewGallery,
  viewPhotoGallery,
} from "../../store/actions/adminActions/addGallery";
import {
  getGalleryAlbums,
  getGalleryYears,
} from "../../store/actions/councilActions/galleryAlbumAction";
import Loader from "../Alert/Loader";
import Lightbox from "./Lightbox";
import "./css/gallery.css";

export default function Gallery() {
  const dispatch = useDispatch();
  const { global } = useSelector((state) => state);
  const { gallery } = global;
  const [images, setImages] = useState(null);
  const [videos, setVideos] = useState([]);
  // Which photo the popup is showing; null = popup closed.
  const [lbIndex, setLbIndex] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  // Which album is being shown. "" is everything, which is what the gallery
  // did before albums existed.
  const [album, setAlbum] = useState("");
  const [albums, setAlbums] = useState([]);
  const [year, setYear] = useState("");
  const [years, setYears] = useState([]);

  // Both filters live in the url useLoadMore watches, so either one changing
  // replaces the photos rather than adding to what is already on screen.
  const [loading, data, setData, hasMore] = useLoadMore(
    `admin/gallery/photo?${album ? `category=${album}&` : ""}${
      year ? `year=${year}&` : ""
    }page=`,
    pageNumber
  );

  useEffect(() => {
    dispatch(getGalleryAlbums({ active: 1, type: "photo" })).then((list) =>
      setAlbums((list || []).filter((a) => a.items_count > 0))
    );
    dispatch(getGalleryYears("gallery")).then(setYears);
  }, [dispatch]);

  const pickYear = (value) => {
    setYear(value);
    setPageNumber(1);
  };

  const pickAlbum = (id) => {
    setAlbum(id);
    setPageNumber(1);
  };

  const observer = useRef();
  const lastDataElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  var regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;

  useEffect(async () => {
    const response = await dispatch(viewPhotoGallery());
    // admin/gallery/photo paginates, so the payload is a paginator object
    // ({current_page, data: [...]}). On the error path the action returns a
    // plain [] instead, and spreading its missing .data threw "is not
    // iterable" - which left the whole gallery blank whenever the request
    // failed. Same guard VideoGallery.js already carries.
    const list = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.data?.data)
      ? response.data.data
      : [];
    setImages(list);
  }, [dispatch]);

  return (
    <>
      <div className="gallery-box">
        <div className="container">
          <div className="title-box">
            <h4>Our Gallery</h4>
          </div>

          {/* albums, shown only once there is something to filter by */}
          {years.length > 0 && (
            <div className="album-filter">
              <button
                type="button"
                className={`album-chip ${year === "" ? "active" : ""}`}
                onClick={() => pickYear("")}
              >
                All Years
              </button>
              {years.map((y) => (
                <button
                  type="button"
                  key={y}
                  className={`album-chip ${
                    String(year) === String(y) ? "active" : ""
                  }`}
                  onClick={() => pickYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {albums.length > 0 && (
            <div className="album-filter">
              <button
                type="button"
                className={`album-chip ${album === "" ? "active" : ""}`}
                onClick={() => pickAlbum("")}
              >
                All
              </button>
              {albums.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  className={`album-chip ${
                    String(album) === String(a.id) ? "active" : ""
                  }`}
                  onClick={() => pickAlbum(a.id)}
                >
                  {a.name} <span>({a.items_count})</span>
                </button>
              ))}
            </div>
          )}

          <div className="row my-4">
            {data == null ? (
              <Loader active={true} />
            ) : data.length > 0 ? (
              data.map((gal, index) => {
                if (data.length === index + 1) {
                  return (
                    <div
                      key={index}
                      className="col-12 col-sm-6 mt-3 col-md-3"
                      ref={lastDataElementRef}
                    >
                      <div className="galleryBox text-center p-2">
                        <div
                          title={gal.title}
                          onClick={() => setLbIndex(index)}
                        >
                          <figure class="figure mb-0">
                            <img
                              class="figure-img img-fluid rounded"
                              src={gal.file}
                              alt={gal.title}
                            />
                            <figcaption class="figure-caption  text-white text-center">
                              {gal.title}
                            </figcaption>
                          </figure>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className="col-12 col-sm-6 mt-3 col-md-3"
                      ref={lastDataElementRef}
                    >
                      <div className="galleryBox text-center p-2">
                        <div
                          title={gal.title}
                          onClick={() => setLbIndex(index)}
                        >
                          <figure class="figure mb-0">
                            <img
                              class="figure-img img-fluid rounded"
                              src={gal.file}
                              alt={gal.title}
                            />
                            <figcaption class="figure-caption  text-white text-center">
                              {gal.title}
                            </figcaption>
                          </figure>
                        </div>
                      </div>
                    </div>
                  );
                }
              })
            ) : (
              <h1>No Gallery found</h1>
            )}
          </div>

          {lbIndex !== null && data && data.length > 0 && (
            <Lightbox
              items={data.map((g) => ({
                type: "image",
                src: g.file,
                title: g.title,
              }))}
              index={lbIndex}
              onClose={() => setLbIndex(null)}
              onNav={setLbIndex}
            />
          )}

          {/* {data.length > 0 ? (
            data.map((gal, index) => {
              if (data.length === index + 1) {
                return (
                  <div
                    className="col-md-5 col-sm-12"
                    ref={lastDataElementRef}
                    key={gal}
                  >
                    <div className="galleryBox">
                      <img src={gal.file} alt={gal.title} />
                      <h6 className="text-center">{gal.title}</h6>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="col-md-5 col-sm-12">
                    <div className="galleryBox">
                      <img src={gal.file} alt={gal.title} />
                      <h6 className="text-center">{gal.title}</h6>
                    </div>
                  </div>
                );
              }
            })
          ) : (
            <div></div>
          )}
          {!loading && data.length == 0 && <div>No News Available</div>}
          {loading && <div>Loading...</div>} */}

          {/* <div className="row">
            {images == null ? (
              <Loader active={true} />
            ) : images.length > 0 ? (
              images.map((gal) => (
                <div className="col-md-5 col-sm-12">
                  <div className="galleryBox">
                    <img src={gal.file} alt={gal.title} />
                    <h6 className="text-center">{gal.title}</h6>
                  </div>
                </div>
              ))
            ) : (
              <h1>No Gallery found</h1>
            )}
          </div> */}
          {/* </div> */}
          {/* <div className="row"> */}
          {/* <div className="col-lg-6 col-md-12 col-sm-12">
              <div className="row">
                {videos.map((video) => (
                  <div className="col-md-6 col-sm-12">
                    <div className="galleryBox">
                      <iframe
                        width={"100%"}
                        height={"100%"}
                        src={`//www.youtube.com/embed/${
                          video.video_url.match(regExp) &&
                          video.video_url.match(regExp)[2].length == 11 &&
                          video.video_url.match(regExp)[2]
                        }`}
                        allowFullScreen
                      ></iframe>
                      <h6 className="text-center">{video.title}</h6>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
        </div>
      </div>
    </>
  );
}
